import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  activateAccount,
  fetchHouseAccess,
  login,
  lookupEmailStatus,
  setSessionHouse,
  clearSession,
} from '../../../platform/auth/session';
import { Icon } from '../../../components/ui/Icon';
import { AuthAside } from '../AuthAside';
import { FirstHouseOnboarding } from '../FirstHouseOnboarding';
import type {
  EmailAccessStatus,
  SessionHouse,
  StoredSession,
} from '../../../platform/auth/session';

type Step = 'email' | 'password' | 'activate' | 'houses' | 'first-house';

export function LoginPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState<Step>('email');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [activationSecret, setActivationSecret] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [session, setSession] = useState<StoredSession | null>(null);
  const [houses, setHouses] = useState<SessionHouse[]>([]);
  const [emailStatus, setEmailStatus] = useState<EmailAccessStatus | null>(null);
  const [canCreateFirstHouse, setCanCreateFirstHouse] = useState(false);

  function enterHouse(house: SessionHouse) {
    setSessionHouse(house);
    void navigate('/', { replace: true });
  }

  async function afterAuth(created: StoredSession) {
    const access = await fetchHouseAccess(created);
    const available = access.houses;
    if (available.length === 0) {
      setSession(created);
      setCanCreateFirstHouse(access.onboarding.canCreateHouse);
      setStep('first-house');
      return;
    }
    if (available.length === 1) {
      const only = available[0];
      if (only) enterHouse(only);
      return;
    }
    setSession(created);
    setHouses(available);
    setStep('houses');
  }

  async function handleEmailContinue(event: { preventDefault: () => void }) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const status = await lookupEmailStatus(email);
      setEmailStatus(status);
      if (status === 'unknown') {
        setError(
          'Não encontramos uma conta ou convite para este e-mail. Confira o endereço ou procure o suporte do Zeladoria.',
        );
        return;
      }
      if (status === 'invited') {
        setStep('activate');
        return;
      }
      setStep('password');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Não foi possível consultar o e-mail.');
    } finally {
      setBusy(false);
    }
  }

  async function handlePasswordLogin(event: { preventDefault: () => void }) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const created = await login(email, password);
      await afterAuth(created);
    } catch (err) {
      clearSession();
      setError(err instanceof Error ? err.message : 'Não foi possível entrar.');
    } finally {
      setBusy(false);
    }
  }

  async function handleActivate(event: { preventDefault: () => void }) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const created = await activateAccount(email, activationSecret, password);
      await afterAuth(created);
    } catch (err) {
      clearSession();
      setError(err instanceof Error ? err.message : 'Não foi possível ativar o acesso.');
    } finally {
      setBusy(false);
    }
  }

  function resetToEmail() {
    setStep('email');
    setPassword('');
    setActivationSecret('');
    setEmailStatus(null);
    setError(null);
  }

  const subtitle =
    step === 'first-house'
      ? 'Acesso inicial à casa'
      : step === 'houses'
        ? 'Escolha a casa para entrar'
        : step === 'activate'
          ? 'Ative seu acesso com o código recebido'
          : step === 'password'
            ? 'Digite sua senha'
            : 'Entre com o seu e-mail Stavitel';

  return (
    <div className="auth-page">
      <AuthAside />

      <main className="auth-main">
        <div className="auth-card">
          <h2>
            {step === 'email'
              ? 'Bem-vindo de volta.'
              : step === 'first-house'
                ? 'Vamos começar.'
                : 'Quase lá.'}
          </h2>
          <p>{session && step === 'houses' ? 'Escolha a casa para entrar' : subtitle}</p>

          {error && (
            <p role="alert" className="form-error">
              {error}
            </p>
          )}

          {step === 'houses' && session ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
              {houses.map((house) => (
                <button
                  key={house.id}
                  type="button"
                  className="btn btn-outline"
                  onClick={() => {
                    enterHouse(house);
                  }}
                  style={{ justifyContent: 'space-between', minHeight: 52 }}
                >
                  <span style={{ display: 'flex', alignItems: 'center', gap: '0.7rem' }}>
                    <span className="icon-box lime" style={{ width: 32, height: 32 }}>
                      <Icon name="home" size={16} />
                    </span>
                    {house.name}
                  </span>
                  <Icon name="arrow" size={16} />
                </button>
              ))}
            </div>
          ) : null}

          {step === 'first-house' && session ? (
            <FirstHouseOnboarding
              session={session}
              canCreateHouse={canCreateFirstHouse}
              onEnterHouse={enterHouse}
              onChooseHouses={(available) => {
                setHouses(available);
                setStep('houses');
              }}
            />
          ) : null}

          {step === 'email' ? (
            <form
              onSubmit={(event) => {
                void handleEmailContinue(event);
              }}
            >
              <div className="field">
                <label htmlFor="login-email">Seu e-mail</label>
                <input
                  id="login-email"
                  type="email"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                  }}
                  autoComplete="email"
                  placeholder="voce@exemplo.com"
                  required
                />
              </div>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={busy}
                style={{ width: '100%', minHeight: 48 }}
              >
                {busy ? 'Consultando…' : 'Continuar'}
                <Icon name="arrow" size={16} />
              </button>
            </form>
          ) : null}

          {step === 'password' ? (
            <form
              onSubmit={(event) => {
                void handlePasswordLogin(event);
              }}
            >
              <p style={{ fontSize: '0.8rem', color: 'var(--muted)', marginBottom: '1rem' }}>
                Conta: <strong style={{ color: 'var(--ink)' }}>{email}</strong>
                {emailStatus ? ` (${emailStatus})` : ''}
              </p>
              <div className="field">
                <label htmlFor="login-password">Senha</label>
                <input
                  id="login-password"
                  type="password"
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                  }}
                  autoComplete="current-password"
                  required
                />
              </div>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={busy}
                style={{ width: '100%', minHeight: 48 }}
              >
                {busy ? 'Entrando…' : 'Entrar'}
                <Icon name="arrow" size={16} />
              </button>
              <button
                type="button"
                className="text-button"
                onClick={resetToEmail}
                style={{ marginTop: '0.9rem' }}
              >
                Usar outro e-mail
              </button>
            </form>
          ) : null}

          {step === 'activate' ? (
            <form
              onSubmit={(event) => {
                void handleActivate(event);
              }}
            >
              <p style={{ fontSize: '0.8rem', color: 'var(--muted)', marginBottom: '1rem' }}>
                Convite para <strong style={{ color: 'var(--ink)' }}>{email}</strong>. Abra o link
                do convite enviado ao seu e-mail ou cole abaixo o código de ativação e defina sua
                senha.
              </p>
              <div className="field">
                <label htmlFor="login-code">Código de ativação</label>
                <input
                  id="login-code"
                  type="text"
                  value={activationSecret}
                  onChange={(e) => {
                    setActivationSecret(e.target.value);
                  }}
                  autoComplete="one-time-code"
                  required
                />
              </div>
              <div className="field">
                <label htmlFor="login-new-password">Nova senha</label>
                <input
                  id="login-new-password"
                  type="password"
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                  }}
                  autoComplete="new-password"
                  minLength={8}
                  required
                />
              </div>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={busy}
                style={{ width: '100%', minHeight: 48 }}
              >
                {busy ? 'Ativando…' : 'Ativar e entrar'}
                <Icon name="arrow" size={16} />
              </button>
              <button
                type="button"
                className="text-button"
                onClick={resetToEmail}
                style={{ marginTop: '0.9rem' }}
              >
                Usar outro e-mail
              </button>
            </form>
          ) : null}

          <p
            style={{
              fontSize: '0.72rem',
              color: 'var(--muted)',
              marginTop: '1.6rem',
              display: 'flex',
              alignItems: 'center',
              gap: '0.4rem',
              flexWrap: 'wrap',
            }}
          >
            <Icon name="lock" size={14} />
            Ainda não conhece?
            <a
              href="https://www.usezeladoria.com.br"
              style={{ textDecoration: 'underline', textUnderlineOffset: 3 }}
            >
              Conheça o Zeladoria
            </a>
          </p>
        </div>
      </main>
    </div>
  );
}
